import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PostsSection({ user }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewPost, setPreviewPost] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePost, setSharePost] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [platformConfigs, setPlatformConfigs] = useState({});
  const [formattedContent, setFormattedContent] = useState({});
  const [loadingFormat, setLoadingFormat] = useState(false);

  // Use current_industry for admin switcher, fallback to industry
  const effectiveIndustry = user.current_industry || user.industry;

  useEffect(() => {
    fetchPosts();
    fetchPlatformConfigs();
  }, [effectiveIndustry]); // Refetch when industry changes

  const fetchPlatformConfigs = async () => {
    try {
      const response = await axios.get(`${API}/platforms/configs`);
      setPlatformConfigs(response.data.configs);
    } catch (error) {
      console.error('Error fetching platform configs:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // Fetch posts filtered by current industry
      const response = await axios.get(`${API}/posts/user/${user.id}`, {
        params: { industry: effectiveIndustry }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      await axios.delete(`${API}/posts/${postId}`);
      setPosts(posts.filter(post => post.id !== postId));
      alert('✅ Post deleted successfully!');
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('❌ Failed to delete post: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadPost = (post) => {
    try {
      const content = `Platform: ${post.platform}\nIndustry: ${post.industry}\nCreated: ${new Date(post.created_at).toLocaleString()}\n\n${post.content}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `novafeed-post-${post.platform}-${Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('✅ Post downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      alert('❌ Failed to download post');
    }
  };

  const handlePreview = async (post) => {
    setPreviewPost(post);
    setFormattedContent({});
    
    // Fetch formatted content for both platforms
    setLoadingFormat(true);
    try {
      const [fbResponse, igResponse] = await Promise.all([
        axios.post(`${API}/posts/${post.id}/format`, null, { params: { platform: 'Facebook' } }),
        axios.post(`${API}/posts/${post.id}/format`, null, { params: { platform: 'Instagram' } })
      ]);
      
      setFormattedContent({
        Facebook: fbResponse.data.content,
        Instagram: igResponse.data.content
      });
    } catch (error) {
      console.error('Error formatting content:', error);
      // Fallback to original content
      setFormattedContent({
        Facebook: post.content,
        Instagram: post.content
      });
    } finally {
      setLoadingFormat(false);
    }
  };

  const handleShare = async (post) => {
    setSharePost(post);
    setShowShareModal(true);
    
    // Pre-format content for all platforms
    if (!formattedContent[post.id]) {
      try {
        const [fbResponse, igResponse, twResponse] = await Promise.all([
          axios.post(`${API}/posts/${post.id}/format`, null, { params: { platform: 'Facebook' } }),
          axios.post(`${API}/posts/${post.id}/format`, null, { params: { platform: 'Instagram' } }),
          axios.post(`${API}/posts/${post.id}/format`, null, { params: { platform: 'Twitter' } })
        ]);
        
        setFormattedContent(prev => ({
          ...prev,
          [post.id]: {
            Facebook: fbResponse.data.content,
            Instagram: igResponse.data.content,
            Twitter: twResponse.data.content
          }
        }));
      } catch (error) {
        console.error('Error formatting content for share:', error);
      }
    }
  };

  const formatForPlatform = async (post, platform) => {
    try {
      const response = await axios.post(`${API}/posts/${post.id}/format?platform=${platform}`);
      return response.data;
    } catch (error) {
      console.error('Error formatting post:', error);
      return null;
    }
  };

  const downloadForPlatform = async (post, platform) => {
    try {
      // Get or fetch formatted content
      let formatted;
      if (formattedContent[post.id]?.[platform]) {
        formatted = {
          content: formattedContent[post.id][platform],
          image_base64: post.image_base64
        };
      } else {
        // Fetch if not already formatted
        const response = await axios.post(`${API}/posts/${post.id}/format`, null, { params: { platform } });
        formatted = response.data;
      }

      if (!formatted) {
        alert('Failed to format post');
        return;
      }

      // Download formatted image
      if (formatted.image_base64 || post.image_base64) {
        const link = document.createElement('a');
        link.href = `data:image/jpeg;base64,${formatted.image_base64 || post.image_base64}`;
        link.download = `${platform.toLowerCase()}-post-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Download caption as text with formatted content
      const captionBlob = new Blob([formatted.content], { type: 'text/plain' });
      const captionUrl = URL.createObjectURL(captionBlob);
      const captionLink = document.createElement('a');
      captionLink.href = captionUrl;
      captionLink.download = `${platform.toLowerCase()}-caption-${Date.now()}.txt`;
      document.body.appendChild(captionLink);
      captionLink.click();
      document.body.removeChild(captionLink);
      URL.revokeObjectURL(captionUrl);

      alert(`✅ Downloaded ${platform} optimized image + caption!`);
    } catch (error) {
      console.error('Download error:', error);
      alert(`❌ Failed to download: ${error.message}`);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('✅ Copied to clipboard!');
    }).catch(() => {
      alert('❌ Failed to copy');
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="text-6xl mb-4">📝</div>
        <h3 className="text-xl font-bold accent-text mb-2">No posts yet</h3>
        <p className="style={{ color: 'var(--text-muted)' }}">Create your first AI-powered post!</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold gradient-text">My Posts ({posts.length})</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Optimize for Facebook & Instagram</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            className="px-3 py-2 theme-input border rounded-lg text-sm focus:ring-2 outline-none"
            style={{ '--tw-ring-color': 'var(--accent-from)' }}
          >
            <option value="All">All Platforms</option>
            <option value="Facebook">📘 Facebook</option>
            <option value="Instagram">📸 Instagram</option>
            <option value="Twitter">🐦 Twitter</option>
            <option value="LinkedIn">💼 LinkedIn</option>
          </select>
          <button
            onClick={fetchPosts}
            className="px-4 py-2 text-sm font-medium accent-text transition"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <div
            key={post.id}
            className="theme-card border rounded-xl shadow-lg overflow-hidden hover:shadow-xl hover:border-orange-500/50 transition card-hover"
          >
            {post.image_base64 && (
              <img
                src={`data:image/png;base64,${post.image_base64}`}
                alt="Post"
                className="w-full h-48 object-cover cursor-pointer"
                onClick={() => handlePreview(post)}
              />
            )}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'var(--accent-from)', color: '#ffffff', opacity: 0.9 }}>
                  {post.platform}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {new Date(post.created_at).toLocaleDateString()}
                </span>
              </div>
              
              <p className="text-sm mb-4 line-clamp-3" style={{ color: 'var(--text-secondary)' }}>
                {post.content || 'No content'}
              </p>

              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handlePreview(post)}
                  className="px-3 py-2 text-xs font-medium border rounded-lg transition"
                  style={{ color: '#60a5fa', borderColor: '#3b82f6' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.2)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  👁️ Preview
                </button>
                <button
                  onClick={() => handleShare(post)}
                  className="px-3 py-2 text-xs font-medium border rounded-lg transition"
                  style={{ color: '#c084fc', borderColor: '#a855f7' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(168, 85, 247, 0.2)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  📤 Share
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="px-3 py-2 text-xs font-medium border rounded-lg transition"
                  style={{ color: '#f87171', borderColor: '#ef4444' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Modal with Platform Tabs */}
      {previewPost && (
        <div className="fixed inset-0 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}>
          <div className="theme-card border-2 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" style={{ borderColor: 'var(--accent-from)' }}>
            {/* Modal Header */}
            <div className="border-b px-6 py-4 sticky top-0 z-10" style={{ 
              background: `linear-gradient(to right, rgba(var(--accent-from-rgb), 0.2), rgba(var(--accent-to-rgb), 0.2))`,
              borderColor: 'var(--accent-from)',
              backgroundColor: 'var(--card-bg)'
            }}>
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold accent-text">👁️ Post Preview</h3>
                <button
                  onClick={() => setPreviewPost(null)}
                  className="transition text-2xl"
                  style={{ color: 'var(--text-muted)' }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Platform Tabs */}
            <div className="flex border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--secondary-bg)' }}>
              <button
                onClick={() => setSelectedPlatform('Facebook')}
                className={`flex-1 px-6 py-3 font-medium transition ${
                  selectedPlatform === 'Facebook'
                    ? 'text-blue-400 border-b-2 border-blue-500 bg-blue-500/10'
                    : ''
                }`}
                style={selectedPlatform !== 'Facebook' ? { color: 'var(--text-muted)' } : {}}
                onMouseEnter={(e) => {
                  if (selectedPlatform !== 'Facebook') e.target.style.backgroundColor = 'var(--hover-bg)';
                }}
                onMouseLeave={(e) => {
                  if (selectedPlatform !== 'Facebook') e.target.style.backgroundColor = 'transparent';
                }}
              >
                📘 Facebook
              </button>
              <button
                onClick={() => setSelectedPlatform('Instagram')}
                className={`flex-1 px-6 py-3 font-medium transition ${
                  selectedPlatform === 'Instagram'
                    ? 'text-pink-400 border-b-2 border-pink-500 bg-pink-500/10'
                    : ''
                }`}
                style={selectedPlatform !== 'Instagram' ? { color: 'var(--text-muted)' } : {}}
                onMouseEnter={(e) => {
                  if (selectedPlatform !== 'Instagram') e.target.style.backgroundColor = 'var(--hover-bg)';
                }}
                onMouseLeave={(e) => {
                  if (selectedPlatform !== 'Instagram') e.target.style.backgroundColor = 'transparent';
                }}
              >
                📸 Instagram
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {selectedPlatform === 'Facebook' && (
                <div className="space-y-4">
                  {/* Facebook Style Preview */}
                  <div className="bg-white rounded-lg shadow-lg max-w-2xl mx-auto">
                    <div className="p-4">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {user.brand_name?.[0] || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-base">{user.brand_name || 'Your Brand'}</p>
                          <div className="flex items-center text-xs text-gray-500">
                            <span>Just now</span>
                            <span className="mx-1">·</span>
                            <span>🌎</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-900 text-base mb-4 whitespace-pre-line leading-relaxed">
                        {loadingFormat ? (
                          <p className="text-gray-500 italic">✨ Formatting content...</p>
                        ) : (
                          formattedContent.Facebook || previewPost.content || 'No content'
                        )}
                      </div>
                    </div>
                    {previewPost.image_base64 && (
                      <img
                        src={`data:image/png;base64,${previewPost.image_base64}`}
                        alt="Post"
                        className="w-full"
                        style={{ maxHeight: '500px', objectFit: 'cover' }}
                      />
                    )}
                    <div className="px-4 py-3 border-t border-gray-200">
                      <div className="flex items-center justify-around text-gray-600 text-sm font-medium">
                        <button className="flex items-center space-x-2 hover:bg-gray-100 px-4 py-2 rounded-lg transition">
                          <span>👍</span>
                          <span>Like</span>
                        </button>
                        <button className="flex items-center space-x-2 hover:bg-gray-100 px-4 py-2 rounded-lg transition">
                          <span>💬</span>
                          <span>Comment</span>
                        </button>
                        <button className="flex items-center space-x-2 hover:bg-gray-100 px-4 py-2 rounded-lg transition">
                          <span>↗️</span>
                          <span>Share</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-sm style={{ color: 'var(--text-muted)' }}">Facebook Preview · Optimized 1200x630px</p>
                </div>
              )}

              {selectedPlatform === 'Instagram' && (
                <div className="space-y-4">
                  {/* Instagram Style Preview */}
                  <div className="bg-white rounded-lg shadow-lg max-w-md mx-auto">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center text-white font-bold border-2 border-white shadow">
                          {user.brand_name?.[0] || 'U'}
                        </div>
                        <p className="font-semibold text-gray-900">{user.brand_name?.toLowerCase().replace(/\s+/g, '') || 'yourbrand'}</p>
                      </div>
                      <button className="text-gray-900 font-bold">⋯</button>
                    </div>
                    {previewPost.image_base64 && (
                      <img
                        src={`data:image/png;base64,${previewPost.image_base64}`}
                        alt="Post"
                        className="w-full"
                        style={{ aspectRatio: '1/1', objectFit: 'cover' }}
                      />
                    )}
                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex space-x-4">
                          <button className="text-2xl hover:opacity-50 transition">❤️</button>
                          <button className="text-2xl hover:opacity-50 transition">💬</button>
                          <button className="text-2xl hover:opacity-50 transition">📨</button>
                        </div>
                        <button className="text-2xl hover:opacity-50 transition">🔖</button>
                      </div>
                      <p className="font-semibold text-sm text-gray-900 mb-1">1,234 likes</p>
                      <div className="text-sm text-gray-900 leading-relaxed">
                        <span className="font-semibold">{user.brand_name?.toLowerCase().replace(/\s+/g, '') || 'yourbrand'}</span>
                        {' '}
                        {loadingFormat ? (
                          <span className="text-gray-500 italic">✨ Formatting...</span>
                        ) : (
                          <span className="whitespace-pre-line">{formattedContent.Instagram || previewPost.content || 'No content'}</span>
                        )}
                      </div>
                      <p className="text-xs style={{ color: 'var(--text-muted)' }} mt-2">Just now</p>
                    </div>
                  </div>
                  <p className="text-center text-sm style={{ color: 'var(--text-muted)' }}">Instagram Preview · Optimized 1080x1080px</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setPreviewPost(null);
                    handleShare(previewPost);
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition"
                >
                  📤 Share
                </button>
                <button
                  onClick={() => downloadPost(previewPost)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition"
                >
                  💾 Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && sharePost && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-purple-500/50 rounded-2xl max-w-md w-full shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-b border-purple-500/30 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">📤 Share Post</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="style={{ color: 'var(--text-muted)' }} hover:text-white transition text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <p className="style={{ color: 'var(--text-muted)' }} text-sm">Content auto-formatted for each platform with emojis, hashtags & location!</p>
              
              {/* Platform Formatted Content Preview */}
              {formattedContent[sharePost?.id] && (
                <div className="theme-card-nested border rounded-lg p-4 max-h-40 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs style={{ color: 'var(--text-muted)' }}">Platform Content Preview:</span>
                    <select 
                      className="text-xs bg-gray-700 text-white rounded px-2 py-1 border border-gray-600"
                      onChange={(e) => {
                        const platform = e.target.value;
                        const content = formattedContent[sharePost.id]?.[platform] || sharePost.content;
                        document.getElementById('formatted-preview').innerText = content;
                      }}
                    >
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="Twitter">Twitter</option>
                    </select>
                  </div>
                  <p id="formatted-preview" className="text-sm text-white whitespace-pre-line">
                    {formattedContent[sharePost.id]?.Facebook || sharePost.content}
                  </p>
                </div>
              )}
              
              {/* Platform Download Options */}
              <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
                <h4 className="text-orange-400 font-medium mb-3 text-sm">📥 Download Optimized</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => downloadForPlatform(sharePost, 'Facebook')}
                    className="px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition text-sm"
                  >
                    <div>📘 Facebook</div>
                    <div className="text-xs opacity-75">1200x630px</div>
                  </button>
                  <button
                    onClick={() => downloadForPlatform(sharePost, 'Instagram')}
                    className="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition text-sm"
                  >
                    <div>📸 Instagram</div>
                    <div className="text-xs opacity-75">1080x1080px</div>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Downloads image + caption text file</p>
              </div>

              {/* Share Options */}
              <div className="space-y-2">
                <h4 className="style={{ color: 'var(--text-muted)' }} font-medium text-sm">📤 Quick Share</h4>
                <button
                  onClick={() => {
                    const content = formattedContent[sharePost.id]?.Facebook || sharePost.content;
                    copyToClipboard(content);
                  }}
                  className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-700 transition text-left flex items-center justify-between"
                >
                  <span>📋 Copy Facebook Content</span>
                </button>
                
                <button
                  onClick={() => {
                    const content = formattedContent[sharePost.id]?.Facebook || sharePost.content;
                    const text = encodeURIComponent(content);
                    window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank');
                  }}
                  className="w-full px-4 py-2 bg-blue-800 text-white rounded-lg text-sm hover:bg-blue-900 transition text-left"
                >
                  📘 Share on Facebook
                </button>
                
                <button
                  onClick={() => {
                    const content = formattedContent[sharePost.id]?.Twitter || sharePost.content;
                    const text = encodeURIComponent(content);
                    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition text-left"
                >
                  🐦 Share on Twitter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostsSection;
